/**
 * TapToAsk — wraps an English sentence; each word becomes a tap target that
 * summons an AI explanation popover. v4 fix: Chinese content is rendered as
 * plain text (not tappable) — there's no useful word-level explanation for
 * single Chinese characters, and the previous behavior produced one giant
 * dotted underline spanning the whole sentence.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from '@/lib/gsap';
import { explainWord, type WordExplanation } from '@/lib/aiExplain';
import { useAppStore } from '@/lib/store';

interface Props {
  sentence: string;
  className?: string;
  tone?: 'cyan' | 'pink' | 'lime';
}

/** Detect "mostly Chinese" so we know whether word-level tap makes sense. */
function isMostlyChinese(text: string): boolean {
  const total = text.length;
  if (total === 0) return false;
  let han = 0;
  for (const ch of text) {
    if (/[一-鿿]/.test(ch)) han += 1;
  }
  return han / total > 0.25;
}

export function TapToAsk({ sentence, className = '', tone = 'cyan' }: Props): JSX.Element {
  const [open, setOpen] = useState<{ word: string; rect: DOMRect } | null>(null);

  // Chinese / mixed → render as plain text. Tapping individual hanzi is noise.
  if (isMostlyChinese(sentence)) {
    return <span className={className}>{sentence}</span>;
  }

  const tokens = sentence.split(/(\s+)/);

  const accent =
    tone === 'pink' ? 'decoration-ember-400/40 hover:decoration-ember-400 hover:text-ember-300'
    : tone === 'lime' ? 'decoration-mint-400/40 hover:decoration-mint-400 hover:text-mint-300'
    : 'decoration-honey-400/40 hover:decoration-honey-400 hover:text-honey-300';

  return (
    <>
      <span className={className}>
        {tokens.map((t, i) => {
          if (/^\s+$/.test(t)) return <span key={i}>{t}</span>;
          return (
            <button
              key={i}
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setOpen({ word: t, rect });
              }}
              className={`relative underline underline-offset-4 decoration-dotted transition-colors ${accent}`}
            >
              {t}
            </button>
          );
        })}
      </span>

      {open !== null && (
        <ExplainPopover
          key={open.word + open.rect.left}
          word={open.word}
          sentence={sentence}
          rect={open.rect}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function ExplainPopover({
  word,
  sentence,
  rect,
  onClose,
}: {
  word: string;
  sentence: string;
  rect: DOMRect;
  onClose: () => void;
}): JSX.Element {
  const [exp, setExp] = useState<WordExplanation | null>(null);
  const [quotaOk, setQuotaOk] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const consumeAi = useAppStore((s) => s.consumeAiQuota);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!consumeAi()) {
      setQuotaOk(false);
      return;
    }
    explainWord(word, sentence).then(setExp).catch(() => setError('AI 暂时无法回答'));
  }, [word, sentence, consumeAi]);

  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (el === null) return;
    gsap.fromTo(el,
      { opacity: 0, y: -14, scale: 0.85, filter: 'blur(6px)' },
      { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.5, ease: 'back.out(2.4)' },
    );
  }, []);

  // On phones we shrink to fit the viewport; on tablet+ keep the 360px width.
  const PAD = 12;
  const desiredWidth = Math.min(360, window.innerWidth - PAD * 2);
  const top = rect.bottom + window.scrollY + 12;
  const left = Math.min(
    Math.max(PAD, rect.left + rect.width / 2 - desiredWidth / 2),
    window.innerWidth - desiredWidth - PAD,
  );

  return (
    <>
      <div className="fixed inset-0 z-[70]" onClick={onClose} />
      <div
        ref={popoverRef}
        className="fixed z-[80] rounded-2xl glass-card p-4 shadow-2xl border border-honey-400/30"
        style={{ top, left, width: desiredWidth, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-1.5 py-0.5 rounded font-arcade text-honey-300 border border-honey-400/40">AI · 速通助教</span>
          <span className="text-xs text-cream-400">点谁就讲谁</span>
          <button onClick={onClose} className="ml-auto text-cream-500 hover:text-white" aria-label="关闭">×</button>
        </div>

        {!quotaOk ? (
          <div className="py-4 text-center">
            <div className="text-2xl mb-2">⏳</div>
            <div className="text-sm text-cream-200 font-semibold">今日 AI 配额用尽</div>
            <div className="text-xs text-cream-400 mt-1">免费版 30 次/日 · 升级 VIP 享 300 次/日</div>
          </div>
        ) : error !== null ? (
          <div className="py-4 text-center text-ember-300 text-sm">{error}</div>
        ) : exp === null ? (
          <LoadingState word={word} />
        ) : (
          <ExplanationContent exp={exp} />
        )}
      </div>
    </>
  );
}

function LoadingState({ word }: { word: string }): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-cream-300">
        <span className="font-display text-2xl font-extrabold text-honey-300">{word}</span>
        <span className="inline-flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-honey-400 animate-pulse-soft" />
          <span className="h-1.5 w-1.5 rounded-full bg-honey-400 animate-pulse-soft" style={{ animationDelay: '120ms' }} />
          <span className="h-1.5 w-1.5 rounded-full bg-honey-400 animate-pulse-soft" style={{ animationDelay: '240ms' }} />
        </span>
      </div>
      <div className="h-3 rounded bg-white/[0.05] animate-pulse-soft" />
      <div className="h-3 w-3/4 rounded bg-white/[0.05] animate-pulse-soft" />
      <div className="h-3 w-1/2 rounded bg-white/[0.05] animate-pulse-soft" />
    </div>
  );
}

function ExplanationContent({ exp }: { exp: WordExplanation }): JSX.Element {
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-baseline gap-2">
        <span className="font-display text-2xl font-extrabold text-honey-300">{exp.word}</span>
        {exp.ipa !== undefined && <span className="font-mono text-xs text-cream-400">{exp.ipa}</span>}
        <span className="text-xs text-cream-500 uppercase tracking-wider">{exp.pos}</span>
      </div>
      <div className="text-cream-100">{exp.zh}</div>
      <div className="rounded-xl bg-honey-500/[0.06] border border-honey-400/20 px-3 py-2 text-cream-200/95 leading-relaxed">
        💡 {exp.why}
      </div>
      {exp.collocations !== undefined && exp.collocations.length > 0 && (
        <div>
          <div className="text-xs text-cream-400 mb-1.5">常用搭配</div>
          <div className="flex flex-wrap gap-1.5">
            {exp.collocations.map((c) => (
              <span key={c} className="text-xs font-mono px-2 py-0.5 rounded bg-white/[0.05] text-cream-200 border border-white/[0.08]">{c}</span>
            ))}
          </div>
        </div>
      )}
      {exp.examples !== undefined && exp.examples.length > 0 && (
        <div className="space-y-1">
          {exp.examples.map((ex) => (
            <div key={ex.en} className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2">
              <div className="font-mono text-mint-200">{ex.en}</div>
              <div className="text-xs text-cream-400 mt-0.5">{ex.zh}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
